import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle, GitCompare, XCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { InlineLoader } from '../../components/ui/LoadingSpinner'
import { ScoreBreakdown, ScoreCircle } from '../../components/ui/ScoreBar'
import { candidatesApi, jobsApi } from '../../lib/api'
import { capitalize, formatDate } from '../../lib/utils'
import toast from 'react-hot-toast'
import useAuthStore from '../../store/authStore'
import { JobDetailsPanel } from '../../components/jobs/JobDetailsPanel'
import { useLocale } from '../../i18n/LocaleProvider'

function normaliseDisplayList(values) {
  if (!Array.isArray(values)) return []
  return [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
}

function mergeCandidateDetail(current, next) {
  if (!current) return next || null
  if (!next) return current

  return {
    ...current,
    ...next,
    skills: next.skills?.length ? next.skills : current.skills || [],
    missingSkills: next.missingSkills?.length ? next.missingSkills : current.missingSkills || [],
    matchedTerms: next.matchedTerms?.length ? next.matchedTerms : current.matchedTerms || [],
    workHistory: next.workHistory?.length ? next.workHistory : current.workHistory || [],
    educationItems:
      next.educationItems?.length ? next.educationItems : current.educationItems || [],
    certifications:
      next.certifications?.length ? next.certifications : current.certifications || [],
    scoreBreakdown: next.scoreBreakdown || current.scoreBreakdown || null,
    aiEvaluation: next.aiEvaluation || current.aiEvaluation || null,
    summary: next.summary || current.summary || '',
    whyThisScore: next.whyThisScore || current.whyThisScore || '',
    bestMatchingChunkPreview:
      next.bestMatchingChunkPreview || current.bestMatchingChunkPreview || '',
    strengths: next.strengths?.length ? next.strengths : current.strengths || [],
    redFlags: next.redFlags?.length ? next.redFlags : current.redFlags || [],
  }
}

function getCandidateAiEvaluation(candidate) {
  return (
    candidate.aiEvaluation ||
    candidate.ai_evaluation ||
    candidate.scoreBreakdown?.aiEvaluation ||
    candidate.scoreBreakdown?.ai_evaluation ||
    null
  )
}

function getCandidateRecommendation(candidate) {
  const aiEvaluation = getCandidateAiEvaluation(candidate)
  return String(
    candidate.recommendation ||
      aiEvaluation?.recommendation ||
      candidate.modelRecommendation ||
      aiEvaluation?.modelRecommendation ||
      'partial'
  )
    .trim()
    .toLowerCase()
}

function getRecommendationVariant(recommendation) {
  if (recommendation === 'strong') return 'green'
  if (recommendation === 'good') return 'blue'
  if (recommendation === 'partial') return 'yellow'
  if (recommendation === 'weak') return 'red'
  return 'gray'
}

function formatRecommendationLabel(recommendation, t) {
  const normalized = String(recommendation || '').trim().toLowerCase()
  if (['strong', 'good', 'partial', 'weak'].includes(normalized)) {
    return t(`authenticated.recommendations.${normalized}`)
  }
  return capitalize(normalized || 'partial')
}

function getCandidateMatchedEvidence(candidate) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const mustHave = breakdown.mustHave || breakdown.must_have || {}
  const skillMatch = breakdown.skillMatch || breakdown.skill_match || {}
  const experience = breakdown.experience || {}

  return normaliseDisplayList([
    ...(candidate.matchedTerms || []),
    ...(candidate.skills || []),
    ...(mustHave.matchedTerms || mustHave.matched_terms || []),
    ...(mustHave.supportingTerms || mustHave.supporting_terms || []),
    ...(skillMatch.matchedTerms || skillMatch.matched_terms || []),
    ...(experience.experienceEvidence || experience.experience_evidence || []),
    ...(experience.detected_experience_evidence || []),
  ]).slice(0, 12)
}

function getCandidateMissingEvidence(candidate, matchedEvidence) {
  const breakdown = candidate.scoreBreakdown || candidate.score_breakdown || {}
  const mustHave = breakdown.mustHave || breakdown.must_have || {}
  const matchedSet = new Set(
    normaliseDisplayList(matchedEvidence).map((value) => value.toLowerCase())
  )
  const missingTerms = normaliseDisplayList(
    mustHave.extractedTerms || mustHave.extracted_terms || []
  ).filter((value) => !matchedSet.has(value.toLowerCase()))

  return normaliseDisplayList([
    ...(candidate.missingSkills || []),
    ...missingTerms,
    ...(candidate.redFlags || []),
  ]).slice(0, 12)
}

function getComparisonCandidateIds(searchParams, locationState) {
  const queryIds = String(searchParams.get('ids') || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
  const stateIds = Array.isArray(locationState?.candidateIds)
    ? locationState.candidateIds
    : Array.isArray(locationState?.candidates)
    ? locationState.candidates.map((candidate) => candidate?.candidateId)
    : []

  return [...new Set((queryIds.length ? queryIds : stateIds).filter(Boolean))]
}

export function JobComparison() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const [job, setJob] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [reloadVersion, setReloadVersion] = useState(0)
  const { locale, t } = useLocale()

  const candidateIds = useMemo(
    () => getComparisonCandidateIds(searchParams, location.state),
    [location.state, searchParams]
  )
  const initialCandidates = useMemo(
    () => (Array.isArray(location.state?.candidates) ? location.state.candidates : []),
    [location.state]
  )
  const showDebugScoring = user?.isPlatformAdmin === true

  useEffect(() => {
    let cancelled = false

    async function loadComparison() {
      if (candidateIds.length < 2) {
        toast.error(t('authenticated.comparison.toasts.minimum'))
        navigate(`/dashboard/jobs/${jobId}`, { replace: true })
        return
      }

      setLoading(true)
      try {
        const [jobData, resolvedCandidates] = await Promise.all([
          jobsApi.get(jobId).catch(() => null),
          Promise.all(
            candidateIds.map(async (candidateId) => {
              const initialCandidate =
                initialCandidates.find((candidate) => candidate?.candidateId === candidateId) ||
                null
              try {
                const detail = await candidatesApi.getDetail(jobId, candidateId)
                return mergeCandidateDetail(initialCandidate, detail)
              } catch (error) {
                if (initialCandidate) return initialCandidate
                throw error
              }
            })
          ),
        ])

        const hydratedCandidates = resolvedCandidates.filter(Boolean)

        if (!cancelled) {
          if (hydratedCandidates.length < 2) {
            toast.error(t('authenticated.comparison.toasts.notEnough'))
            navigate(`/dashboard/jobs/${jobId}`, { replace: true })
            return
          }

          setJob(jobData)
          setCandidates(hydratedCandidates)
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || t('authenticated.comparison.toasts.loadFailed'))
          navigate(`/dashboard/jobs/${jobId}`, { replace: true })
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadComparison()

    return () => {
      cancelled = true
    }
  }, [candidateIds, initialCandidates, jobId, navigate, reloadVersion])

  async function handleRequirementsSave({ requirements, rescoreCandidates }) {
    try {
      const result = await jobsApi.update(jobId, {
        requirements,
        rescoreCandidates,
        locale,
      })
      if (result?.job) {
        setJob(result.job)
      }
      setReloadVersion((value) => value + 1)
      toast.success(
        rescoreCandidates
          ? t('authenticated.comparison.toasts.requirementsSavedRescore')
          : t('authenticated.comparison.toasts.requirementsSaved')
      )
      if (result?.rescore && result.rescore.success === false) {
        toast.error(result.rescore.error || t('authenticated.comparison.toasts.rescoreWarning'))
      }
      return result
    } catch (err) {
      toast.error(err.message || t('authenticated.comparison.toasts.requirementsFailed'))
      throw err
    }
  }

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => navigate(`/dashboard/jobs/${jobId}`)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('authenticated.comparison.backToJob')}
      </button>

      <div className="card p-5 mb-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <GitCompare className="w-5 h-5 text-brand-600" />
              <h1 className="text-xl font-bold text-gray-900">{t('authenticated.comparison.title')}</h1>
            </div>
            <p className="text-sm text-gray-500">
              {job?.title || t('authenticated.comparison.fallbackJob')} - {t('authenticated.comparison.selected', { count: candidateIds.length })}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/dashboard/jobs/${jobId}`)}
          >
            {t('authenticated.comparison.close')}
          </Button>
        </div>
      </div>

      <div className="mb-5">
        <JobDetailsPanel job={job} onSaveRequirements={handleRequirementsSave} />
      </div>

      {loading ? (
        <InlineLoader text={t('authenticated.comparison.loading')} />
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
          {candidates.map((candidate) => {
            const recommendation = getCandidateRecommendation(candidate)
            const matchedEvidence = getCandidateMatchedEvidence(candidate)
            const missingEvidence = getCandidateMissingEvidence(candidate, matchedEvidence)
            const strengths = normaliseDisplayList(candidate.strengths).slice(0, 6)
            const summary = candidate.summary || candidate.whyThisScore || candidate.whyThisMatch

            return (
              <div key={candidate.candidateId} className="card p-5 space-y-5">
                <div className="flex items-start gap-4">
                  <ScoreCircle score={candidate.matchScore || 0} size="lg" />
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-bold text-gray-900 truncate">
                      {candidate.name || t('authenticated.comparison.unknownCandidate')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {candidate.currentRole || t('authenticated.comparison.roleNotProvided')}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Badge variant={getRecommendationVariant(recommendation)}>
                        {t('authenticated.comparison.recommendation', {
                          label: formatRecommendationLabel(recommendation, t),
                        })}
                      </Badge>
                      <StatusBadge status={candidate.status} />
                      {candidate.experienceYears > 0 && (
                        <Badge variant="gray">
                          {t('authenticated.comparison.yearsExperience', {
                            count: candidate.experienceYears,
                            plural: candidate.experienceYears === 1 ? '' : 's',
                          })}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {summary && (
                  <div className="rounded-xl border border-brand-100 bg-brand-50 p-4">
                    <p className="text-xs font-semibold text-brand-700 mb-1">{t('authenticated.comparison.summary')}</p>
                    <p className="text-sm text-brand-900 leading-relaxed">{summary}</p>
                  </div>
                )}

                {candidate.scoreBreakdown && (
                  <div className="rounded-xl border border-gray-100 p-4">
                    <p className="text-xs font-semibold text-gray-500 mb-3">{t('authenticated.comparison.scoreBreakdown')}</p>
                    <ScoreBreakdown candidate={candidate} showDebug={showDebugScoring} />
                  </div>
                )}

                {strengths.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.comparison.strengths')}</p>
                    <ul className="space-y-1">
                      {strengths.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {matchedEvidence.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.comparison.matchedEvidence')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {matchedEvidence.map((item) => (
                        <span
                          key={item}
                          className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {missingEvidence.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">{t('authenticated.comparison.gaps')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {missingEvidence.map((item) => (
                        <span
                          key={item}
                          className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {candidate.bestMatchingChunkPreview && (
                  <div className="rounded-xl border border-green-100 bg-green-50 p-4">
                    <p className="text-xs font-semibold text-green-700 mb-1">{t('authenticated.comparison.evidenceExcerpt')}</p>
                    <p className="text-sm text-green-900 whitespace-pre-wrap">
                      {candidate.bestMatchingChunkPreview}
                    </p>
                  </div>
                )}

                {(candidate.email || candidate.phone || candidate.updatedAt) && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {candidate.email && (
                      <div className="rounded-xl bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">{t('authenticated.comparison.email')}</p>
                        <p className="text-sm font-semibold text-gray-900 break-all">
                          {candidate.email}
                        </p>
                      </div>
                    )}
                    {candidate.phone && (
                      <div className="rounded-xl bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">{t('authenticated.comparison.phone')}</p>
                        <p className="text-sm font-semibold text-gray-900">{candidate.phone}</p>
                      </div>
                    )}
                    {candidate.updatedAt && (
                      <div className="rounded-xl bg-gray-50 p-3 sm:col-span-2">
                        <p className="text-xs text-gray-500">{t('authenticated.comparison.lastUpdated')}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(candidate.updatedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!loading && candidates.length === 0 && (
        <div className="card p-6 mt-5 text-center">
          <XCircle className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900">{t('authenticated.comparison.emptyTitle')}</p>
          <p className="text-sm text-gray-500 mt-1">
            {t('authenticated.comparison.emptyDescription')}
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => navigate(`/dashboard/jobs/${jobId}`)}
          >
            {t('authenticated.comparison.backToJob')}
          </Button>
        </div>
      )}
    </div>
  )
}
